/**
 * Toolbar — barre d'outils du RichTextEditor.
 *
 * Couvre exactement ce que Markdown peut représenter :
 *   - Bloc-type (dropdown) : Corps de texte, Titre 1/2/3
 *   - Inline : gras, italique, barré
 *   - Listes : à puces, numérotée
 *   - Citation
 *   - Insertions : lien (modal), tableau (modal avec grille de prévisualisation)
 *   - Tableau (sous-toolbar contextuelle, visible uniquement quand le curseur
 *     est dans un tableau) : ajout/suppression de lignes et colonnes, suppression du tableau
 *   - Historique : undo / redo
 *
 * Note : pas de boutons "bloc de code", "code inline" ni "séparateur horizontal"
 * — non utiles pour la KB textuelle. Les nodes correspondants (CodeNode,
 * HorizontalRuleNode) restent néanmoins enregistrés côté RichTextEditor pour
 * que les documents existants contenant ces éléments se rendent correctement.
 *
 * Les tableaux sont gérés séparément à l'Étape D (TablePlugin.tsx).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $setBlocksType } from "@lexical/selection";
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  type HeadingTagType,
} from "@lexical/rich-text";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { $isCodeNode } from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  $deleteTableColumn__EXPERIMENTAL,
  $deleteTableRow__EXPERIMENTAL,
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  $isTableNode,
  INSERT_TABLE_COMMAND,
} from "@lexical/table";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";

import {
  BetweenHorizontalEnd,
  BetweenHorizontalStart,
  BetweenVerticalEnd,
  BetweenVerticalStart,
  Bold,
  ChevronDown,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  MinusSquare,
  Quote,
  Redo,
  Strikethrough,
  Table2,
  Trash,
  Undo,
} from "lucide-react";

import { LinkModal } from "./LinkModal";
import { TableInsertModal } from "./TableInsertModal";

// Les valeurs paragraph/h1/h2/h3 correspondent aux options du dropdown.
// Les autres ("quote", "code", "bullet", "number", "other") sont des états
// internes pour griser/ajuster l'UI (ex. le bouton Citation est actif).
type BlockType =
  | "paragraph"
  | "h1"
  | "h2"
  | "h3"
  | "quote"
  | "code"
  | "bullet"
  | "number"
  | "other";

interface BlockOption {
  value: "paragraph" | "h1" | "h2" | "h3";
  label: string;
}

const BLOCK_OPTIONS: BlockOption[] = [
  { value: "paragraph", label: "Corps de texte" },
  { value: "h1", label: "Titre 1" },
  { value: "h2", label: "Titre 2" },
  { value: "h3", label: "Titre 3" },
];

function blockLabel(type: BlockType): string {
  switch (type) {
    case "h1":
      return "Titre 1";
    case "h2":
      return "Titre 2";
    case "h3":
      return "Titre 3";
    case "paragraph":
      return "Corps de texte";
    case "quote":
      return "Citation";
    case "code":
      return "Bloc de code";
    case "bullet":
      return "Liste à puces";
    case "number":
      return "Liste numérotée";
    default:
      return "Mise en forme";
  }
}

interface ToolbarBtnProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarBtn({ onClick, active, disabled, title, children }: ToolbarBtnProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active ? "true" : "false"}
      title={title}
      aria-label={title}
      className={"rte-tb-btn" + (active ? " is-active" : "")}
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return <span className="rte-tb-sep" aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// BlockTypeDropdown — sous-composant inline
// ---------------------------------------------------------------------------

interface BlockTypeDropdownProps {
  current: BlockType;
  onSelect: (value: BlockOption["value"]) => void;
}

function BlockTypeDropdown({ current, onSelect }: BlockTypeDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Ferme au clic dehors + sur Escape
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="rte-tb-block-dd" ref={containerRef}>
      <button
        type="button"
        className={"rte-tb-block-trigger" + (open ? " is-open" : "")}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Type de bloc"
      >
        <span>{blockLabel(current)}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rte-tb-block-menu" role="listbox">
          {BLOCK_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-type={opt.value}
              role="option"
              aria-selected={current === opt.value}
              className={"rte-tb-block-item" + (current === opt.value ? " is-selected" : "")}
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar principale
// ---------------------------------------------------------------------------

export function Toolbar() {
  const [editor] = useLexicalComposerContext();

  // Marques inline actives sur la sélection courante
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  // Type de bloc courant
  const [blockType, setBlockType] = useState<BlockType>("paragraph");
  // État du lien (utile pour préremplir la modale)
  const [linkSelection, setLinkSelection] = useState<{
    isLink: boolean;
    currentUrl: string;
    selectedText: string;
  }>({ isLink: false, currentUrl: "", selectedText: "" });
  // Historique
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  // Modale lien
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  // Modale insertion tableau
  const [tableModalOpen, setTableModalOpen] = useState(false);
  // Curseur dans un tableau → affiche la sous-toolbar contextuelle
  const [isInTable, setIsInTable] = useState(false);

  // --- Synchronisation état toolbar ←→ sélection Lexical ---
  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }

    // Marques inline
    setIsBold(selection.hasFormat("bold"));
    setIsItalic(selection.hasFormat("italic"));
    setIsStrikethrough(selection.hasFormat("strikethrough"));

    // Type de bloc — on remonte à l'élément top-level
    const anchorNode = selection.anchor.getNode();
    const element =
      anchorNode.getKey() === "root"
        ? anchorNode
        : ($findMatchingParent(anchorNode, (e) => {
            const parent = e.getParent();
            return parent !== null && $isRootOrShadowRoot(parent);
          }) ?? anchorNode.getTopLevelElementOrThrow());

    if ($isHeadingNode(element)) {
      const tag = element.getTag();
      setBlockType(tag === "h1" || tag === "h2" || tag === "h3" ? tag : "other");
    } else if ($isQuoteNode(element)) {
      setBlockType("quote");
    } else if ($isCodeNode(element)) {
      setBlockType("code");
    } else if ($isListNode(element)) {
      const listType = (element as ListNode).getListType();
      setBlockType(listType === "number" ? "number" : "bullet");
    } else {
      const parentList = $findMatchingParent(anchorNode, $isListNode) as ListNode | null;
      if (parentList) {
        setBlockType(parentList.getListType() === "number" ? "number" : "bullet");
      } else {
        setBlockType("paragraph");
      }
    }

    // Détection : le curseur est-il dans un tableau ?
    setIsInTable($findMatchingParent(anchorNode, $isTableNode) !== null);

    // Détection lien sur la sélection
    const linkNode = $findMatchingParent(anchorNode, $isLinkNode);
    if (linkNode) {
      setLinkSelection({
        isLink: true,
        currentUrl: (linkNode as { getURL: () => string }).getURL(),
        selectedText: selection.getTextContent(),
      });
    } else {
      setLinkSelection({
        isLink: false,
        currentUrl: "",
        selectedText: selection.getTextContent(),
      });
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => updateToolbar());
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, updateToolbar]);

  // --- Actions ---

  const formatText = (mark: "bold" | "italic" | "strikethrough") => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, mark);
  };

  // Applique le type de bloc choisi depuis le dropdown.
  // Si on choisit ce qu'on a déjà, on ne fait rien (UX neutre).
  const applyBlockType = (value: BlockOption["value"]) => {
    if (value === blockType) return;
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      if (value === "paragraph") {
        $setBlocksType(selection, () => $createParagraphNode());
      } else {
        $setBlocksType(selection, () => $createHeadingNode(value as HeadingTagType));
      }
    });
  };

  const toggleQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (blockType === "quote") {
          $setBlocksType(selection, () => $createParagraphNode());
        } else {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      }
    });
  };

  const toggleBulletList = () => {
    if (blockType === "bullet") {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  };

  const toggleNumberList = () => {
    if (blockType === "number") {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    }
  };

  // --- Tableau ---

  const openTableModal = () => setTableModalOpen(true);

  const confirmInsertTable = (rows: number, columns: number) => {
    // Note GFM : la première ligne sert d'en-tête. On demande donc à Lexical
    // de créer une row d'en-têtes (rows: true) mais pas de colonne d'en-tête
    // (columns: false), conformément au standard tableau Markdown.
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns: String(columns),
      rows: String(rows),
      includeHeaders: { rows: true, columns: false },
    });
    setTableModalOpen(false);
  };

  const insertRow = (after: boolean) => {
    editor.update(() => {
      $insertTableRow__EXPERIMENTAL(after);
    });
  };

  const insertColumn = (after: boolean) => {
    editor.update(() => {
      $insertTableColumn__EXPERIMENTAL(after);
    });
  };

  const deleteRow = () => {
    editor.update(() => {
      $deleteTableRow__EXPERIMENTAL();
    });
  };

  const deleteColumn = () => {
    editor.update(() => {
      $deleteTableColumn__EXPERIMENTAL();
    });
  };

  const deleteTable = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const tableNode = $findMatchingParent(selection.anchor.getNode(), $isTableNode);
      if (tableNode) tableNode.remove();
    });
  };

  // --- Lien ---

  const openLinkModal = () => setLinkModalOpen(true);

  const confirmLink = (url: string) => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
    setLinkModalOpen(false);
  };

  const removeLink = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    setLinkModalOpen(false);
  };

  // --- Historique ---

  const doUndo = () => editor.dispatchCommand(UNDO_COMMAND, undefined);
  const doRedo = () => editor.dispatchCommand(REDO_COMMAND, undefined);

  return (
    <>
      <div className="rte-toolbar" role="toolbar" aria-label="Mise en forme">
        {/* Historique */}
        <ToolbarBtn onClick={doUndo} disabled={!canUndo} title="Annuler (Ctrl+Z)">
          <Undo size={15} />
        </ToolbarBtn>
        <ToolbarBtn onClick={doRedo} disabled={!canRedo} title="Rétablir (Ctrl+Shift+Z)">
          <Redo size={15} />
        </ToolbarBtn>

        <ToolbarSep />

        {/* Type de bloc (dropdown) */}
        <BlockTypeDropdown current={blockType} onSelect={applyBlockType} />

        <ToolbarSep />

        {/* Marques inline */}
        <ToolbarBtn
          onClick={() => formatText("bold")}
          active={isBold}
          title="Gras (Ctrl+B)"
        >
          <Bold size={15} />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => formatText("italic")}
          active={isItalic}
          title="Italique (Ctrl+I)"
        >
          <Italic size={15} />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => formatText("strikethrough")}
          active={isStrikethrough}
          title="Barré"
        >
          <Strikethrough size={15} />
        </ToolbarBtn>

        <ToolbarSep />

        {/* Listes */}
        <ToolbarBtn
          onClick={toggleBulletList}
          active={blockType === "bullet"}
          title="Liste à puces"
        >
          <List size={15} />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={toggleNumberList}
          active={blockType === "number"}
          title="Liste numérotée"
        >
          <ListOrdered size={15} />
        </ToolbarBtn>

        <ToolbarSep />

        {/* Citation */}
        <ToolbarBtn
          onClick={toggleQuote}
          active={blockType === "quote"}
          title="Citation"
        >
          <Quote size={15} />
        </ToolbarBtn>

        <ToolbarSep />

        {/* Lien + Tableau */}
        <ToolbarBtn
          onClick={openLinkModal}
          active={linkSelection.isLink}
          title="Insérer un lien"
        >
          <LinkIcon size={15} />
        </ToolbarBtn>
        <ToolbarBtn onClick={openTableModal} title="Insérer un tableau">
          <Table2 size={15} />
        </ToolbarBtn>
      </div>

      {/* Sous-toolbar contextuelle — visible quand le curseur est dans un tableau */}
      {isInTable && (
        <div
          className="rte-toolbar rte-toolbar-context"
          role="toolbar"
          aria-label="Actions du tableau"
        >
          <span className="rte-tb-context-label">Tableau</span>
          <ToolbarSep />
          <ToolbarBtn
            onClick={() => insertRow(false)}
            title="Ajouter une ligne au-dessus"
          >
            <BetweenHorizontalStart size={15} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => insertRow(true)}
            title="Ajouter une ligne en-dessous"
          >
            <BetweenHorizontalEnd size={15} />
          </ToolbarBtn>
          <ToolbarBtn onClick={deleteRow} title="Supprimer la ligne courante">
            <MinusSquare size={15} />
          </ToolbarBtn>
          <ToolbarSep />
          <ToolbarBtn
            onClick={() => insertColumn(false)}
            title="Ajouter une colonne à gauche"
          >
            <BetweenVerticalStart size={15} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => insertColumn(true)}
            title="Ajouter une colonne à droite"
          >
            <BetweenVerticalEnd size={15} />
          </ToolbarBtn>
          <ToolbarBtn onClick={deleteColumn} title="Supprimer la colonne courante">
            <MinusSquare size={15} style={{ transform: "rotate(90deg)" }} />
          </ToolbarBtn>
          <ToolbarSep />
          <ToolbarBtn onClick={deleteTable} title="Supprimer le tableau">
            <Trash size={15} />
          </ToolbarBtn>
        </div>
      )}

      <LinkModal
        open={linkModalOpen}
        initialUrl={linkSelection.currentUrl}
        selectedText={linkSelection.selectedText}
        onConfirm={confirmLink}
        onRemove={linkSelection.isLink ? removeLink : undefined}
        onCancel={() => setLinkModalOpen(false)}
      />

      <TableInsertModal
        open={tableModalOpen}
        onConfirm={confirmInsertTable}
        onCancel={() => setTableModalOpen(false)}
      />
    </>
  );
}
